'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function BestPracticesPage() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />

      <div className="relative container mx-auto px-4 py-16 pt-32 max-w-5xl">
        {/* Back button */}
        <Link
          href="/en/resources/documentation"
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300  transition-all mb-12"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Back to Documentation</span>
        </Link>
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight mb-6">
            Best Practices
          </h1>
          <p className="text-2xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Production-ready patterns and recommendations for enterprise PostgreSQL backup management.
          </p>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="absolute -inset-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl rounded-2xl -z-10" />
          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">

        <h2>Backup Strategy</h2>

        <h3>Recommended Backup Schedule</h3>
        <table>
          <thead>
            <tr>
              <th>Backup Type</th>
              <th>Frequency</th>
              <th>Retention</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Full</td>
              <td>Weekly (Sunday 2 AM)</td>
              <td>4 weeks</td>
              <td>Baseline for all restores</td>
            </tr>
            <tr>
              <td>Differential</td>
              <td>Daily (2 AM)</td>
              <td>7 days</td>
              <td>Daily recovery points</td>
            </tr>
            <tr>
              <td>Incremental</td>
              <td>Every 4 hours</td>
              <td>48 hours</td>
              <td>Minimal data loss</td>
            </tr>
            <tr>
              <td>WAL Archive</td>
              <td>Continuous</td>
              <td>7 days</td>
              <td>Point-in-time recovery</td>
            </tr>
          </tbody>
        </table>

        <h3>Retention Policy Guidelines</h3>
        <ul>
          <li><strong>Production:</strong> 30 days minimum, 90 days recommended</li>
          <li><strong>Staging:</strong> 14 days</li>
          <li><strong>Development:</strong> 7 days</li>
          <li><strong>Compliance:</strong> Follow regulatory requirements (often 7 years)</li>
        </ul>

        <h2>Performance Optimization</h2>

        <h3>Backup Window Planning</h3>
        <p>Schedule backups during low-traffic periods:</p>
        <ul>
          <li>Analyze database load patterns</li>
          <li>Avoid peak business hours</li>
          <li>Consider timezone differences for global operations</li>
          <li>Stagger backups across multiple servers</li>
        </ul>

        <h3>Resource Allocation</h3>
        <pre><code>{`# Recommended settings for large databases (>1TB)
process-max=8
compress-type=lz4
compress-level=1
buffer-size=16MB`}</code></pre>

        <h3>Network Optimization</h3>
        <ul>
          <li>Use dedicated backup network when possible</li>
          <li>Enable compression for remote backups</li>
          <li>Consider bandwidth throttling during business hours</li>
          <li>Use local staging for cloud uploads</li>
        </ul>

        <h2>Security</h2>

        <h3>Encryption Standards</h3>
        <ul>
          <li><strong>At rest:</strong> AES-256-CBC minimum</li>
          <li><strong>In transit:</strong> TLS 1.3 for all connections</li>
          <li><strong>Key management:</strong> Use HSM or cloud KMS for production</li>
        </ul>

        <h3>Access Control</h3>
        <pre><code>{`# Principle of least privilege
backup_user:
  - Can read database
  - Can write to backup location
  - Cannot modify production data
  - Cannot access other databases`}</code></pre>

        <h3>Audit Requirements</h3>
        <p>Log and monitor:</p>
        <ul>
          <li>All backup operations (success and failure)</li>
          <li>Restore operations</li>
          <li>Configuration changes</li>
          <li>Access to backup files</li>
          <li>Encryption key usage</li>
        </ul>

        <h2>Disaster Recovery</h2>

        <h3>RTO and RPO Targets</h3>
        <table>
          <thead>
            <tr>
              <th>Tier</th>
              <th>RTO</th>
              <th>RPO</th>
              <th>Strategy</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Critical</td>
              <td>&lt; 1 hour</td>
              <td>&lt; 5 minutes</td>
              <td>Hot standby + PITR</td>
            </tr>
            <tr>
              <td>Important</td>
              <td>&lt; 4 hours</td>
              <td>&lt; 1 hour</td>
              <td>Warm standby + frequent backups</td>
            </tr>
            <tr>
              <td>Standard</td>
              <td>&lt; 24 hours</td>
              <td>&lt; 24 hours</td>
              <td>Daily backups</td>
            </tr>
          </tbody>
        </table>

        <h3>DR Testing Schedule</h3>
        <ul>
          <li><strong>Monthly:</strong> Verify backup integrity</li>
          <li><strong>Quarterly:</strong> Full restore drill</li>
          <li><strong>Annually:</strong> Complete DR failover test</li>
          <li><strong>After changes:</strong> Test any infrastructure modifications</li>
        </ul>

        <h2>Monitoring</h2>

        <h3>Critical Alerts</h3>
        <p>Configure immediate alerts for:</p>
        <ul>
          <li>Backup failure (any type)</li>
          <li>No successful backup in 24 hours</li>
          <li>Storage capacity &gt; 80%</li>
          <li>Backup duration increase &gt; 50%</li>
          <li>Replication lag &gt; 5 minutes</li>
        </ul>

        <h3>Dashboard Metrics</h3>
        <p>Monitor these KPIs:</p>
        <ul>
          <li><strong>Success rate:</strong> 7-day rolling average</li>
          <li><strong>Backup size trend:</strong> Predict storage needs</li>
          <li><strong>Duration trend:</strong> Identify performance degradation</li>
          <li><strong>Recovery time:</strong> Track restore performance</li>
        </ul>

        <h2>Compliance</h2>

        <h3>GDPR Requirements</h3>
        <ul>
          <li>Implement data retention policies</li>
          <li>Support right to erasure (backup deletion)</li>
          <li>Encrypt personal data in backups</li>
          <li>Document data processing activities</li>
          <li>Maintain audit logs for 3 years</li>
        </ul>

        <h3>SOC 2 Controls</h3>
        <ul>
          <li>Encrypt backups at rest and in transit</li>
          <li>Implement role-based access control</li>
          <li>Enable comprehensive audit logging</li>
          <li>Test disaster recovery procedures</li>
          <li>Document backup policies and procedures</li>
        </ul>

        <h3>HIPAA Compliance</h3>
        <ul>
          <li>Encrypt all PHI in backups (AES-256)</li>
          <li>Implement access controls and audit logs</li>
          <li>Sign Business Associate Agreements</li>
          <li>Conduct regular risk assessments</li>
          <li>Maintain backup integrity verification</li>
        </ul>

        <h2>Cost Optimization</h2>

        <h3>Storage Tiering</h3>
        <ul>
          <li><strong>Hot storage:</strong> Last 7 days (fast access)</li>
          <li><strong>Warm storage:</strong> 8-30 days (moderate cost)</li>
          <li><strong>Cold storage:</strong> 31+ days (lowest cost)</li>
          <li><strong>Archive:</strong> Compliance retention (cheapest)</li>
        </ul>

        <h3>Compression Strategy</h3>
        <pre><code>{`# Balance speed vs. size
Recent backups: lz4 (fast restore)
Older backups: zst (better compression)
Archive: zst level 9 (maximum compression)`}</code></pre>

        <h2>Operational Excellence</h2>

        <h3>Documentation Requirements</h3>
        <ul>
          <li>Backup architecture diagram</li>
          <li>Restore procedures (step-by-step)</li>
          <li>Escalation contacts</li>
          <li>Configuration management</li>
          <li>Change log</li>
        </ul>

        <h3>Team Training</h3>
        <ul>
          <li>Quarterly restore drills for all team members</li>
          <li>Document lessons learned from incidents</li>
          <li>Cross-train team members on backup operations</li>
          <li>Maintain runbooks for common scenarios</li>
        </ul>

        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-bold mb-2">Golden Rule</h3>
          <p className="mb-0">
            <strong>A backup is only as good as your last successful restore.</strong> Test your backups regularly and document the process. When disaster strikes, you'll be glad you did.
          </p>
        </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
